import { amber } from '@mui/material/colors'
import { useMap } from '@vis.gl/react-google-maps'
import { throttle } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import parseDriverPathSegments from 'components/common/map/parseDriverPathSegments'
import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'
import { useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import WebGLMarkerSurface from 'components/common/map/webgl/WebGLMarkerSurface'
import { useBreadcrumbInteraction } from 'components/route-manager/RouteManagerMapV2/hooks/useBreadcrumbInteraction'
import { useMapViewState } from 'components/route-manager/RouteManagerMapV2/hooks/useMapViewState'
import DriverMarker from 'components/route-manager/RouteManagerMapV2/markers/DriverMarker'
import BreadcrumbHoverInfo from 'components/route-manager/RouteManagerMapV2/overlays/BreadcrumbHoverInfo'
import Polyline from 'components/route-manager/RouteManagerMapV2/shapes/Polyline'
import { useBreadcrumbLayers } from 'components/route-manager/RouteManagerMapV2/webgl/useBreadcrumbLayers'

const DEFAULT_ROUTE_KEY = 'master-route-driver-path'

// Fixed amber so the line stays visible against the dark basemap regardless of route color (often dark hex).
const DRIVER_PATH_COLOR = amber.A700

// Builds the Route/Driver/Speed/Timestamp tooltip state shared by the DOM and
// WebGL paths, mirroring RouteManagerMapV2's DriverRenderer but scoped to one
// route. Returns null info + a disabled flag when no timestamps are supplied,
// so callers without timestamp data render exactly as before.
function useDriverPathTooltip({ routeKey, breadcrumbsByRouteId, routesById, routeLocationTimestamps, routeName }) {
  const map = useMap()
  const [hoverInfo, setHoverInfo] = useState(null)
  // Tracked separately from hoverInfo so the path thickens the moment the cursor
  // is over it (matching RouteManagerMapV2), even before the nearest GPS sample resolves.
  const [hovered, setHovered] = useState(false)

  const timestampData = useMemo(
    () => (routeLocationTimestamps?.length ? { routeLocationTimestamps: { [routeKey]: routeLocationTimestamps } } : null),
    [routeKey, routeLocationTimestamps]
  )

  const { getNearestPoint } = useBreadcrumbInteraction({ breadcrumbsByRouteId, routesById, timestampData })
  const setHoverInfoThrottled = useMemo(() => throttle(info => setHoverInfo(info), 50), [])

  // Flush any pending throttled update on unmount so it can't setState on an
  // unmounted component (and leak its timer).
  useEffect(() => () => setHoverInfoThrottled.cancel(), [setHoverInfoThrottled])

  const showAt = useCallback(
    (lat, lng, clientX, clientY) => {
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY) || (clientX === 0 && clientY === 0)) return
      const nearest = getNearestPoint(routeKey, lat, lng)
      if (!nearest) {
        setHoverInfoThrottled.cancel()
        setHoverInfo(null)
        return
      }
      setHoverInfoThrottled({
        x: clientX,
        y: clientY,
        timestamp: nearest.timestamp,
        speed: nearest.speed,
        driverName: nearest.driver,
        routeName,
      })
    },
    [getNearestPoint, routeKey, routeName, setHoverInfoThrottled]
  )

  const clear = useCallback(() => {
    setHoverInfoThrottled.cancel()
    setHoverInfo(null)
    setHovered(false)
  }, [setHoverInfoThrottled])

  // When the cursor leaves the map container entirely, deck.gl does NOT fire an
  // unhover on the WebGL hit-layer, so the tooltip would stay stuck. Mirror
  // RouteManagerMapV2 and clear on the native mouseleave.
  useEffect(() => {
    const mapDiv = map?.getDiv?.()
    if (!mapDiv) return undefined
    mapDiv.addEventListener('mouseleave', clear)
    return () => mapDiv.removeEventListener('mouseleave', clear)
  }, [map, clear])

  // DOM polyline hover — Google Maps PolyMouseEvent (latLng + native domEvent).
  const onDomMouseMove = useCallback(
    e => {
      if (!e?.latLng) return
      setHovered(true)
      const lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat
      const lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng
      showAt(lat, lng, e.domEvent?.clientX, e.domEvent?.clientY)
    },
    [showAt]
  )

  // WebGL hit-layer hover — deck.gl gives container-relative x/y; prefer the
  // native event's viewport coords, falling back to the map rect + offset.
  const onRouteHover = useCallback(
    ({ coordinate, x, y, srcEvent }) => {
      if (!coordinate) return
      setHovered(true)
      const [lng, lat] = coordinate
      const rect = map?.getDiv?.()?.getBoundingClientRect?.()
      const clientX = Number.isFinite(srcEvent?.clientX) ? srcEvent.clientX : (rect?.left ?? 0) + (x ?? 0)
      const clientY = Number.isFinite(srcEvent?.clientY) ? srcEvent.clientY : (rect?.top ?? 0) + (y ?? 0)
      showAt(lat, lng, clientX, clientY)
    },
    [showAt, map]
  )

  return { hoverInfo, hovered, enabled: Boolean(timestampData), onDomMouseMove, onRouteHover, clear }
}

// Hook form: returns the parsed driver-path data plus the deck.gl breadcrumb
// layers and (when `routeLocationTimestamps` is supplied) the hover-tooltip
// state. Lets callers compose path layers into a shared overlay (see the legacy
// MasterRouteMapV2) and render their own DriverMarker / BreadcrumbHoverInfo.
export function useDriverPathLayers({ breadcrumb, routeColor, routeId, routeLocationTimestamps, routeName }) {
  const { zoom } = useMapViewState()
  // Accept either a raw firestore breadcrumb (FeatureCollection/Feature/string) or
  // already-parsed segment arrays (the useRoutesDriverPathBreadcrumb hooks return those).
  const segments = useMemo(() => (Array.isArray(breadcrumb) ? breadcrumb : parseDriverPathSegments(breadcrumb)), [breadcrumb])
  const color = routeColor || DRIVER_PATH_COLOR
  const routeKey = routeId || DEFAULT_ROUTE_KEY

  const sortedRouteIds = useMemo(() => [routeKey], [routeKey])
  // segments are already in canonical { coordinates, driver } breadcrumb shape.
  const breadcrumbsByRouteId = useMemo(() => ({ [routeKey]: segments }), [routeKey, segments])
  const routeLayers = useMemo(() => ({ [routeKey]: { routeColor: color } }), [routeKey, color])
  const routesById = useMemo(() => ({ [routeKey]: { color } }), [routeKey, color])

  const tooltip = useDriverPathTooltip({ routeKey, breadcrumbsByRouteId, routesById, routeLocationTimestamps, routeName })

  const layers = useBreadcrumbLayers({
    sortedRouteIds,
    breadcrumbsByRouteId,
    routeLayers,
    routesById,
    zoom,
    hoveredRouteId: tooltip.hovered ? routeKey : undefined,
    onRouteHover: tooltip.enabled ? tooltip.onRouteHover : undefined,
    onRouteUnhover: tooltip.enabled ? tooltip.clear : undefined,
  })

  const driverPosition = useMemo(() => {
    if (segments.length === 0) return null
    const lastCoords = segments[segments.length - 1].coordinates
    const lastCoord = lastCoords[lastCoords.length - 1]
    return { lng: parseFloat(lastCoord[0]), lat: parseFloat(lastCoord[1]) }
  }, [segments])

  return { segments, layers, driverPosition, color, tooltip }
}

// `forceDom` opts out of the WebGL path even when supported — native
// polylines transform smoothly with the basemap during zoom animations,
// while the raster GoogleMapsOverlay canvas clears + repaints late (visible
// blink). Surfaces where the path is a primary visual (master-sequence map)
// trade WebGL batching for that stability.
//
// Pass `routeLocationTimestamps` (the route's GPS samples: { lat, lng, timestamp,
// speedMps }) + `routeName` to surface the hover tooltip with time/speed/driver.
// JSDoc so TS consumers can omit the props they don't have — a destructured JS
// signature otherwise infers every one of them as required.
/**
 * @param {{
 *   breadcrumb?: object|array|string, routeColor?: string, methodName?: string,
 *   routeId?: string, routeLocationTimestamps?: object[], routeName?: string,
 *   forceDom?: boolean,
 * }} props
 */
const DriverPathOverlay = ({
  breadcrumb,
  routeColor,
  methodName,
  routeId,
  routeLocationTimestamps,
  routeName,
  forceDom = false,
  casingColor = null,
}) => {
  const webglSupported = useWebGLSupport()
  const { segments, layers, driverPosition, color, tooltip } = useDriverPathLayers({
    breadcrumb,
    routeColor,
    routeId,
    routeLocationTimestamps,
    routeName,
  })

  if (segments.length === 0 || !driverPosition) return null

  const pathWeight = tooltip.enabled && tooltip.hovered ? 5 : 3

  const domPath = (
    <>
      {segments.map((seg, index) => {
        const path = seg.coordinates.map(c => ({ lat: parseFloat(c[1]), lng: parseFloat(c[0]) }))
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`dom-path-${index}`}>
            {/* A wider line beneath the path, so a thin coloured stroke still reads on a dark basemap. */}
            {casingColor && <Polyline path={path} strokeColor={casingColor} strokeOpacity={0.9} strokeWeight={pathWeight + 4} zIndex={1} />}
            <Polyline
              showArrows
              arrowRepeat="70px"
              path={path}
              strokeColor={color}
              strokeOpacity={0.8}
              strokeWeight={pathWeight}
              zIndex={2}
            />
            {/* Transparent wide hit line so the thin visible path is easy to hover for the tooltip. */}
            {tooltip.enabled && (
              <Polyline
                path={path}
                strokeColor={color}
                strokeOpacity={0}
                strokeWeight={20}
                onMouseMove={tooltip.onDomMouseMove}
                onMouseOut={tooltip.clear}
              />
            )}
          </React.Fragment>
        )
      })}
    </>
  )

  return (
    <>
      {/* DriverMarker first so its slot stays stable even if the path overlay swaps element types when webglSupported flips. */}
      <DriverMarker color={color} method={methodName} position={driverPosition} />
      <WebGLMarkerSurface componentName="DriverPathOverlay" enabled={webglSupported && !forceDom} fallback={domPath}>
        <DeckOverlay key="webgl-path" layers={layers} style={{ zIndex: 2 }} />
      </WebGLMarkerSurface>
      {tooltip.enabled && <BreadcrumbHoverInfo info={tooltip.hoverInfo} />}
    </>
  )
}

DriverPathOverlay.propTypes = {
  breadcrumb: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  routeColor: PropTypes.string,
  methodName: PropTypes.string,
  routeId: PropTypes.string,
  routeLocationTimestamps: PropTypes.arrayOf(PropTypes.object),
  routeName: PropTypes.string,
  forceDom: PropTypes.bool,
  casingColor: PropTypes.string,
}

export default DriverPathOverlay
